import { useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";

import CrmEnrichmentJobFilters from "app/pages/sales-intelligence/modules/crm-enrichment/components/filters/CrmEnrichmentJobFilters";
import { EnrichmentSettingsBreadcrumbs } from "app/pages/sales-intelligence/modules/crm-enrichment/components/EnrichmentSettingsBreadcrumbs/EnrichmentSettingsBreadcrumbs";
import JobType from "app/pages/sales-intelligence/modules/crm-enrichment/components/settings/CrmEnrichmentJobType/JobType";
import JobEditor from "app/pages/sales-intelligence/modules/crm-enrichment/components/settings/JobEditor/JobEditor";
import CrmEnrichmentFieldsMappingWizardStep from "app/pages/sales-intelligence/modules/crm-enrichment/components/wizard/CrmEnrichmentFieldsMappingWizardStep";
import CrmEnrichmentJobSettings from "app/pages/sales-intelligence/modules/crm-enrichment/components/wizard/CrmEnrichmentJobSettings";
import { useCrmEnrichmentContext } from "app/pages/sales-intelligence/modules/crm-enrichment/contexts/CrmEnrichmentContext";
import useFiltersActions from "app/pages/sales-intelligence/modules/crm-enrichment/hooks/useFiltersActions";
import useJobSettingsActions from "app/pages/sales-intelligence/modules/crm-enrichment/hooks/wizard/useJobSettingsActions";
import useStatefullEditValidation from "app/pages/sales-intelligence/modules/crm-enrichment/hooks/wizard/useStatefullEditValidation";
import { SF_JOB_OBJECT_OPTIONS } from "app/pages/sales-intelligence/modules/crm-enrichment/impl/salesforce/constants/enrichment-job";
import SfJobRemoveModalContainer from "app/pages/sales-intelligence/modules/crm-enrichment/impl/salesforce/containers/SfJobRemoveModalContainer";
import useSfEnrichmentJob from "app/pages/sales-intelligence/modules/crm-enrichment/impl/salesforce/hooks/useSfEnrichmentJob";
import {
    selectSfColumnsBySelectedJobObject,
    selectSfColumnsBySelectedJobObjectRaw,
    selectSfCrmFieldOptions,
} from "app/pages/sales-intelligence/modules/crm-enrichment/impl/salesforce/store/selectors";
import crmEnrichmentTrackingService from "app/pages/sales-intelligence/modules/crm-enrichment/services/tracking/trackingService";
import {
    addMapping,
    removeMapping,
    resetWizardFilters,
    setJobName,
    setMappings,
    setRemoveModalOpen,
    updateMapping,
} from "app/pages/sales-intelligence/modules/crm-enrichment/store/action-creators";
import {
    selectJobState,
    selectMappings,
    selectWizardFilters,
} from "app/pages/sales-intelligence/modules/crm-enrichment/store/selectors";
import type {
    CrmEnrichmentJob,
    CrmEnrichmentJobObject,
    CrmEnrichmentJobTabName,
    EnrichmentJobMappingDTO,
} from "app/pages/sales-intelligence/modules/crm-enrichment/types/types";
import { getDefaultMappings } from "app/pages/sales-intelligence/modules/crm-enrichment/utils/defaultMappings";
import { ENRICHMENT_OBJECT_TO_EXPORT_NAME } from "app/pages/sales-intelligence/modules/crm-enrichment/utils/enrichmentObjectToExportName";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { selectSfSettings } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforce/exportSelectors";
import CountryService from "app/services/CountryService";

import useSfEnrichmentJobEditMutation from "../query/mutations/useSfEnrichmentJobEditMutation";
import useSfEnrichmentJobQuery from "../query/queries/useSfEnrichmentJobQuery";

const SfEnrichmentJobEditor = () => {
    const dispatch = useDispatch();

    const { crmProvider, i18nKeys } = useCrmEnrichmentContext();
    const { params } = useCurrentStateAndParams<{ id: string }>();

    const currentJobState = useSelector(selectJobState);
    const wizardFilters = useSelector(selectWizardFilters);
    const mappingColumns = useSelector(selectSfColumnsBySelectedJobObject);
    const filterColumns = useSelector(selectSfColumnsBySelectedJobObjectRaw);
    const crmFieldOptions = useSelector(selectSfCrmFieldOptions);
    const mappings = useSelector(selectMappings);
    const sfSettings = useSelector(selectSfSettings);

    const handleAddMapping = (mapping: EnrichmentJobMappingDTO): void => {
        dispatch(addMapping(mapping));
    };

    const handleRemoveMapping = (index: number): void => {
        dispatch(removeMapping(index));
    };

    const handleUpdateMapping = (index: number, changes: Partial<EnrichmentJobMappingDTO>): void => {
        dispatch(updateMapping({ index, changes }));
    };

    const { data: initialJob, isError: isQueryError } = useSfEnrichmentJobQuery(params.id);

    const { mutate: mutateEdit, isPending: isLoading } = useSfEnrichmentJobEditMutation();

    const { createJobDTO, initializeFiltersFromJob } = useSfEnrichmentJob({ initialJob, columns: filterColumns });

    const { isNextButtonDisabled } = useStatefullEditValidation({
        initialJob,
        columns: filterColumns,
    });

    const filtersActions = useFiltersActions(filterColumns);
    const settingsActions = useJobSettingsActions({
        crmProvider,
        onSelectJobObject(object: CrmEnrichmentJobObject): void {
            const exportSettings = sfSettings[ENRICHMENT_OBJECT_TO_EXPORT_NAME[object]];

            if (object === initialJob?.enrichmentObject) {
                initializeFiltersFromJob(initialJob);
                dispatch(setMappings(initialJob?.mappings ?? getDefaultMappings(crmProvider, object, exportSettings)));
            } else {
                dispatch(resetWizardFilters());
                dispatch(setMappings(getDefaultMappings(crmProvider, object, exportSettings)));
            }
        },
    });

    const goToManagePage = (): void => {
        void siNavigator.goToCrmManagementPage({ crm: crmProvider });
    };

    useEffect(() => {
        if (isQueryError) {
            goToManagePage();
        }
    }, [isQueryError]);

    const handleSaveJob = (): void => {
        if (initialJob?.name !== currentJobState.jobName) {
            crmEnrichmentTrackingService.edit.trackName(currentJobState.jobName, crmProvider, initialJob?.name);
        }

        mutateEdit(createJobDTO() as CrmEnrichmentJob, {
            onSuccess: goToManagePage,
        });
    };

    const handleSetJobName = (name: string): void => {
        dispatch(setJobName(name));
    };

    const handleOpenRemoveModal = (): void => {
        dispatch(setRemoveModalOpen(true));
    };

    const handleTabClick = (tabName: CrmEnrichmentJobTabName) => {
        crmEnrichmentTrackingService.wizard.trackSelectSection(tabName, crmProvider, initialJob?.type);
    };

    if (!initialJob) {
        return null;
    }

    return (
        <>
            <EnrichmentSettingsBreadcrumbs
                crmProvider={crmProvider}
                dataAutomation="sf-enrichment-job-edit-breadcrumbs"
            />
            <JobEditor
                isSaveButtonDisabled={isNextButtonDisabled}
                isLoading={isLoading}
                onSave={handleSaveJob}
                onCancel={goToManagePage}
                onDeleteClick={handleOpenRemoveModal}
                onTabClick={handleTabClick}
                content={{
                    type: (
                        <JobType
                            jobName={currentJobState.jobName}
                            type={initialJob.type}
                            enrichmentSource={initialJob.enrichmentSource}
                            onSetJobName={handleSetJobName}
                        />
                    ),
                    settings: (
                        <CrmEnrichmentJobSettings
                            initialJobObject={initialJob.enrichmentObject}
                            selectedJobObject={currentJobState.jobObject}
                            selectedJobType={initialJob.type}
                            selectedCountry={CountryService.getCountryById(currentJobState.countryId)}
                            selectedJobRefreshPeriod={currentJobState.refreshPeriod}
                            selectedLeadScoreRefreshDataInterval={currentJobState.leadScoreRefreshDataInterval}
                            selectedEnrichmentSource={currentJobState.enrichmentSource}
                            translationKeys={i18nKeys}
                            jobObjectOptions={SF_JOB_OBJECT_OPTIONS}
                            showEnrichmentLevel={false}
                            {...settingsActions}
                        />
                    ),
                    filters: (
                        <div>
                            <CrmEnrichmentJobFilters
                                translationKeys={i18nKeys}
                                {...wizardFilters}
                                {...filtersActions}
                                selectedJobObject={currentJobState.jobObject}
                                crmFieldOptions={crmFieldOptions}
                            />
                        </div>
                    ),
                    "fields-mapping": (
                        <CrmEnrichmentFieldsMappingWizardStep
                            crmProvider={crmProvider}
                            enrichmentObject={(currentJobState.jobObject || "company") as CrmEnrichmentJobObject}
                            jobType={currentJobState.jobType}
                            mappings={mappings}
                            columns={mappingColumns}
                            isColumnsLoading={false}
                            onAddMapping={handleAddMapping}
                            onRemoveMapping={handleRemoveMapping}
                            onUpdateMapping={handleUpdateMapping}
                            showHeader={false}
                        />
                    ),
                }}
            />

            <SfJobRemoveModalContainer
                job={initialJob}
                onSuccess={goToManagePage}
            />
        </>
    );
};

export default SfEnrichmentJobEditor;
